/* eslint-disable */
// src/pages/StaffInviteManager.jsx
// PresentPlus — Staff Invites (Admin only)
// Creates staff invite tokens + emails + tracks status.

import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  Timestamp,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import "../styles/AdminManager.css";

const EXPIRE_MS = 24 * 60 * 60 * 1000;
const RESEND_COOLDOWN_MS = 5 * 60 * 1000;

// Prefer env, fallback to your deployed parent invite endpoint
const STAFF_EMAIL_ENDPOINT =
  process.env.REACT_APP_SEND_STAFF_INVITE_URL ||
  process.env.REACT_APP_SEND_PARENT_INVITE_URL ||
  ""; // set in .env for production

function safeLower(s) {
  return (s || "").toString().trim().toLowerCase();
}

function canResend(inv) {
  if (!inv?.inviteSentAt?.toMillis) return true;
  return Date.now() - inv.inviteSentAt.toMillis() > RESEND_COOLDOWN_MS;
}

function expiryLabel(inv) {
  if (!inv?.inviteExpiresAt?.toMillis) return "";
  const expiresAt = inv.inviteExpiresAt.toMillis();
  const now = Date.now();
  if (now >= expiresAt) return "Expired";
  const diff = expiresAt - now;
  const h = Math.floor(diff / (1000 * 60 * 60));
  const m = Math.floor((diff / (1000 * 60)) % 60);
  return h > 0 ? `Expires in ${h}h ${m}m` : `Expires in ${m}m`;
}

async function postInviteEmail({ email, name, inviteToken }) {
  if (!STAFF_EMAIL_ENDPOINT) {
    throw new Error(
      "Missing STAFF invite email endpoint. Set REACT_APP_SEND_STAFF_INVITE_URL (or REACT_APP_SEND_PARENT_INVITE_URL)."
    );
  }

  const res = await fetch(STAFF_EMAIL_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      name,
      inviteToken,
      // If you want to override link here you can pass inviteLink too
      // inviteLink: `${window.location.origin}/accept-staff-invite?token=${inviteToken}`,
    }),
  });

  if (!res.ok) {
    let msg = "Failed to send email.";
    try {
      const data = await res.json();
      msg = data?.error || msg;
    } catch {}
    throw new Error(msg);
  }
}

export default function StaffInviteManager() {
  const { user, role } = useAuth();

  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [search, setSearch] = useState("");

  const canView = role === "admin";

  const loadInvites = async () => {
    setLoading(true);
    setErr("");
    try {
      const qy = query(
        collection(db, "staffInvites"),
        orderBy("createdAt", "desc"),
        limit(300)
      );
      const snap = await getDocs(qy);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setInvites(list);
    } catch (e) {
      console.error(e);
      setErr("Failed to load staff invites.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || !canView) return;
    loadInvites();
    // eslint-disable-next-line
  }, [user, canView]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return invites;
    return invites.filter((i) => {
      const hay = [i.email, i.name, i.inviteStatus, i.inviteToken]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [invites, search]);

  const createInvite = async () => {
    setErr("");

    const em = safeLower(email);
    if (!em) return setErr("Enter staff email.");
    if (!em.includes("@")) return setErr("Enter a valid email.");

    try {
      const token = `${Date.now()}_${Math.random().toString(36).slice(2)}`;

      const ref = await addDoc(collection(db, "staffInvites"), {
        email: em,
        name: name || "",
        inviteToken: token,
        inviteStatus: "sent",
        inviteSentAt: serverTimestamp(),
        inviteExpiresAt: Timestamp.fromDate(new Date(Date.now() + EXPIRE_MS)),
        createdAt: serverTimestamp(),
        createdByUid: user?.uid || null,
        createdByEmail: user?.email || null,
      });

      await postInviteEmail({ email: em, name, inviteToken: token });

      // optional audit log
      try {
        await addDoc(collection(db, "admin_audit"), {
          action: "staff_invite_sent",
          targetType: "staff",
          targetId: em,
          adminUid: user?.uid || null,
          adminEmail: user?.email || null,
          createdAt: serverTimestamp(),
          metadata: { staffInvitesDocId: ref.id },
        });
      } catch {}

      setName("");
      setEmail("");
      await loadInvites();
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to create invite.");
    }
  };

  const resendInvite = async (inv) => {
    setErr("");

    if (!inv) return;
    if (inv.inviteStatus === "accepted") return setErr("This invite is already accepted.");
    if (!canResend(inv)) return setErr("Please wait a moment before resending.");

    try {
      const newToken = `${Date.now()}_${Math.random().toString(36).slice(2)}`;

      await updateDoc(doc(db, "staffInvites", inv.id), {
        inviteToken: newToken,
        inviteStatus: "sent",
        inviteSentAt: serverTimestamp(),
        inviteExpiresAt: Timestamp.fromDate(new Date(Date.now() + EXPIRE_MS)),
      });

      await postInviteEmail({
        email: inv.email,
        name: inv.name,
        inviteToken: newToken,
      });

      // optional audit log
      try {
        await addDoc(collection(db, "admin_audit"), {
          action: "staff_invite_resent",
          targetType: "staff",
          targetId: inv.email,
          adminUid: user?.uid || null,
          adminEmail: user?.email || null,
          createdAt: serverTimestamp(),
          metadata: { staffInvitesDocId: inv.id },
        });
      } catch {}

      await loadInvites();
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to resend invite.");
    }
  };

  if (!user) {
    return (
      <div className="admin-wrap">
        <div className="admin-card">
          <h2>Please sign in</h2>
          <p>You must be signed in to manage staff invites.</p>
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="admin-wrap">
        <div className="admin-card">
          <h2>Access restricted</h2>
          <p>Only admins can manage staff invites.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-wrap">
      <div className="admin-card">
        <header className="admin-header">
          <div>
            <h1 className="admin-title">Staff Invites</h1>
            <p className="admin-subtitle">Create and resend staff invitations for onboarding.</p>
          </div>

          <div className="admin-actions">
            <button className="primary-btn" type="button" onClick={loadInvites}>
              Refresh
            </button>
          </div>
        </header>

        {err && <div className="error-banner">{err}</div>}

        <div className="admin-grid" style={{ marginTop: 12 }}>
          <div className="admin-panel">
            <h3>Create invite</h3>

            <div className="field-row">
              <label>Name (optional)</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ms Jones" />
            </div>

            <div className="field-row">
              <label>Email</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="staff@school.com" />
            </div>

            <button className="primary-btn" type="button" onClick={createInvite}>
              Send invite
            </button>

            <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
              Tip: set <code>REACT_APP_SEND_STAFF_INVITE_URL</code> in <code>.env</code> to your deployed
              function URL (Cloud Run URL from Firebase Functions).
            </div>
          </div>

          <div className="admin-panel">
            <h3>Invites</h3>

            <div className="field-row">
              <label>Search</label>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="email, token, status…" />
            </div>

            {loading ? (
              <div style={{ padding: 16 }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 16 }}>No invites yet.</div>
            ) : (
              <div className="admin-list">
                {filtered.map((inv) => (
                  <div className="admin-row" key={inv.id}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ fontWeight: 700 }}>{inv.email}</div>
                      <div style={{ fontSize: 12, opacity: 0.85 }}>
                        {(inv.name || "—")} • {(inv.inviteStatus || "unknown")} • {expiryLabel(inv)}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        Token: <code>{inv.inviteToken || "—"}</code>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button
                        className="ghost-btn"
                        type="button"
                        onClick={() => resendInvite(inv)}
                        disabled={!canResend(inv) || inv.inviteStatus === "accepted"}
                        title={!canResend(inv) ? "Cooldown active" : "Resend"}
                      >
                        Resend
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
